
  import { createRoot } from "react-dom/client";
  import * as Sentry from "@sentry/react";
  import App from "./app/App.tsx";
  import "./styles/index.css";
  import "./i18n";

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.MODE,
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.1,
      integrations: [Sentry.browserTracingIntegration()],
      beforeSend(event) {
        // B-57: RangeError: Maximum call stack size exceeded — recurring Realtime bug.
        // Rate-limit to 1 event per 5 minutes to stop flooding the quota while root
        // cause is being investigated. Last event timestamp stored in module scope.
        const excValues = event.exception?.values;
        if (
          excValues?.some(
            (v) =>
              v.type === 'RangeError' &&
              typeof v.value === 'string' &&
              v.value.includes('Maximum call stack')
          )
        ) {
          const now = Date.now();
          const last = (window as any).__sentry_last_stack_err__ ?? 0;
          if (now - last < 5 * 60 * 1000) return null;
          (window as any).__sentry_last_stack_err__ = now;
        }

        // Headers: JWT / apikey / cookie never leave the browser
        if (event.request?.headers) {
          const h = event.request.headers as Record<string, string>;
          delete h['Authorization'];
          delete h['authorization'];
          delete h['apikey'];
          delete h['Cookie'];
          delete h['cookie'];
        }
        if (event.request?.cookies) delete event.request.cookies;

        // Query strings: redact sensitive params (reset_token, OTP code, etc.)
        const SENSITIVE_QS = /([?&])(token|code|access_token|refresh_token|apikey|reset_token|password)=[^&#]*/gi;
        const scrubUrl = (u: unknown) =>
          typeof u === 'string' ? u.replace(SENSITIVE_QS, '$1$2=REDACTED') : u;
        if (event.request?.url) event.request.url = scrubUrl(event.request.url) as string;
        // Request body on the event itself (fetch POST that errored in same tick)
        if (event.request && 'data' in event.request) {
          delete (event.request as Record<string, unknown>).data;
        }
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((b) => {
            if (b.data) {
              if (b.data.url) b.data.url = scrubUrl(b.data.url);
              if (b.data.to) b.data.to = scrubUrl(b.data.to);
              if (b.data.from) b.data.from = scrubUrl(b.data.from);
              // Fetch/XHR breadcrumbs may carry request bodies under several names
              const d = b.data as Record<string, unknown>;
              delete d.body;
              delete d.request_body;
              delete d.request_body_size;
              delete d.__requestBody;
            }
            return b;
          });
        }

        // User PII: email + IP scrubbed; id left for correlation (set upstream if needed)
        if (event.user) {
          if (event.user.email) event.user.email = '[redacted]';
          delete event.user.ip_address;
        }

        return event;
      },
    });
  }

  // Инициализация Telegram Mini App
  const tg = window.Telegram?.WebApp;
  if (tg) {
    tg.ready();
    tg.expand(); // разворачивает приложение на весь экран
  }

  createRoot(document.getElementById("root")!).render(<App />);

  // Stale-shell rescue: if a dynamic import fails (CSS/JS chunk 404 after deploy
  // rotated hashes), reload once to pick up the fresh HTML + new chunk URLs.
  // Guarded by sessionStorage so a truly-missing chunk doesn't reload-loop.
  const CHUNK_RELOAD_KEY = 'moms:chunk-reload-attempted';
  const isChunkLoadError = (msg: string) =>
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|is not a valid JavaScript MIME type/i.test(msg);

  const tryStaleShellReload = () => {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    window.location.reload();
  };

  window.addEventListener('error', (event) => {
    const msg = event?.message || String(event?.error || '');
    if (isChunkLoadError(msg)) tryStaleShellReload();
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const msg = typeof reason === 'string' ? reason : reason?.message || String(reason || '');
    if (isChunkLoadError(msg)) tryStaleShellReload();
  });

  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch((err) => {
        console.warn('SW registration failed:', err);
      });
    });
  }
